import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Pencil, AlertCircle } from "lucide-react";
import { useCanMutate } from "@/lib/rbac";

// Select menu sizing. MENU_MAX_HEIGHT matches the previous `max-h-48` so a long
// option list still scrolls internally instead of running the height of the screen.
const MENU_MAX_HEIGHT = 192;
const MENU_MIN_HEIGHT = 96;
const MENU_MIN_WIDTH = 180;
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

interface EditableDetailFieldProps {
  value: string;
  onChange: (value: string) => void;
  icon: React.ReactNode;
  label?: string;
  isLink?: boolean;
  linkPrefix?: string;
  className?: string;
  suggestions?: string[];
  onDrillDown?: () => void;
  selectOptions?: string[];
  validate?: (value: string) => string | null; // Returns error message or null if valid
  hideIcon?: boolean;
  placeholder?: string;
}

export default function EditableDetailField({
  value, onChange, icon, label, isLink, linkPrefix = "https://", className, suggestions = [], onDrillDown, selectOptions, validate, hideIcon, placeholder,
}: EditableDetailFieldProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [editing, setEditingState] = useState(false);
  const setEditing = (next: boolean) => {
    // Read-only users can never open edit mode.
    if (next && !canMutate) return;
    setEditingState(next);
  };
  const [draft, setDraft] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectTriggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
  } | null>(null);

  const isSelect = !!selectOptions;

  // The select menu is positioned `fixed` against the trigger rect instead of
  // `absolute` inside the row. Detail sheets scroll via `overflow-y-auto` on
  // SheetContent, which makes overflow-x non-visible too, so an absolutely
  // positioned menu gets cropped by the sheet box (reported on Company →
  // Annual Revenue, same for Employees and deal select custom fields).
  // It stays inline in the DOM on purpose — createPortal escapes the Radix
  // Sheet boundary and Radix then dismisses the sheet on option click
  // (docs/COMMON_BUGS.md → "Portal-Rendered Dropdowns Inside Radix Dialog").
  useLayoutEffect(() => {
    if (!editing || !isSelect) return;
    const updatePos = () => {
      const rect = selectTriggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
      // Flip above the trigger when the list would be cut off below and there
      // is more room up there — a row near the bottom of the sheet otherwise
      // shows only its first choice or two.
      const openUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
      const width = Math.max(rect.width, MENU_MIN_WIDTH);
      setMenuPos({
        left: Math.max(
          VIEWPORT_MARGIN,
          Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
        ),
        width,
        maxHeight: Math.max(
          MENU_MIN_HEIGHT,
          Math.min(MENU_MAX_HEIGHT, openUp ? spaceAbove : spaceBelow),
        ),
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + MENU_GAP }
          : { top: rect.bottom + MENU_GAP }),
      });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [editing, isSelect]);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      setShowSuggestions(suggestions.length > 0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, value, suggestions.length]);

  // Close select dropdown on outside click
  useEffect(() => {
    if (editing && selectOptions) {
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setEditing(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [editing, selectOptions]);

  const commit = () => {
    const trimmed = draft.trim();

    // Validate if validator provided and value changed
    if (trimmed && trimmed !== value && validate) {
      const validationError = validate(trimmed);
      if (validationError) {
        setError(validationError);
        return; // Don't close edit mode if validation fails
      }
    }

    setEditing(false);
    setShowSuggestions(false);
    setError(null);
    if (trimmed !== value) onChange(trimmed);
  };

  const filteredSuggestions = suggestions.filter(s =>
    s.toLowerCase() !== draft.toLowerCase() &&
    (!draft.trim() || s.toLowerCase().includes(draft.toLowerCase()))
  ).slice(0, 8);

  const shellClassName = cn(
    "flex items-center gap-2 text-sm group rounded -mx-1 px-1 py-0.5 -my-0.5 transition min-w-0",
    className,
  );

  if (editing && selectOptions) {
    return (
      <div
        className={shellClassName}
        ref={containerRef}
      >
        {!hideIcon && <span className="shrink-0">{icon}</span>}
        {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
        <div className="flex-1 min-w-0 relative">
          <button
            ref={selectTriggerRef}
            className="w-full text-left bg-transparent outline-none text-sm p-0 border-0"
          >
            {draft || t("detailSheets.selectPlaceholder")}
          </button>
          {menuPos && (
            <div
              className="fixed z-[100] rounded-md border border-border bg-popover shadow-md py-1 overflow-y-auto"
              style={{
                top: menuPos.top,
                bottom: menuPos.bottom,
                left: menuPos.left,
                width: menuPos.width,
                maxHeight: menuPos.maxHeight,
              }}
            >
              {selectOptions.map(opt => (
                <button
                  key={opt}
                  onMouseDown={e => { e.preventDefault(); setDraft(opt); onChange(opt); setEditing(false); }}
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 transition truncate",
                    opt === draft && "bg-accent/10 text-accent font-medium"
                  )}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className={cn(
          shellClassName,
          // URL fields expand while editing so the full URL is visible without
          // covering the label column or forcing horizontal scroll in a narrow cell.
          isLink && "absolute left-0 top-0 z-30 bg-popover border border-border shadow-md rounded-md px-2 py-1 min-w-[360px]",
        )}
        ref={containerRef}
      >
        {!hideIcon && <span className="shrink-0">{icon}</span>}
        {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
        <div className="flex-1 min-w-0 relative">
          <div className="space-y-1">
            <input
              ref={inputRef}
              value={draft}
              onChange={e => { setDraft(e.target.value); setShowSuggestions(true); setError(null); }}
              onBlur={() => { setTimeout(() => { setShowSuggestions(false); commit(); }, 150); }}
              onKeyDown={e => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") { setDraft(value); setEditing(false); setShowSuggestions(false); setError(null); }
              }}
              className={cn(
                "block w-full bg-transparent outline-none text-sm p-0 m-0 border-0 focus:ring-0",
                error && "border-b border-destructive"
              )}
            />
            {error && (
              <div className="flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />
                {error}
              </div>
            )}
          </div>
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border border-border bg-popover shadow-md py-1 max-h-40 overflow-y-auto">
              {filteredSuggestions.map(s => (
                <button
                  key={s}
                  onMouseDown={e => { e.preventDefault(); setDraft(s); onChange(s); setEditing(false); setShowSuggestions(false); setError(null); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 transition truncate"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 text-sm group rounded -mx-1 px-1 py-0.5 -my-0.5 transition min-w-0",
        canMutate && "cursor-pointer hover:bg-muted/30",
        className,
      )}
      onClick={e => {
        if (!canMutate) return;
        e.stopPropagation();
        if (!onDrillDown) setEditing(true);
      }}
      title={!canMutate ? t("common.readOnlyCannotChange") : undefined}
    >
      {!hideIcon && <span className="shrink-0">{icon}</span>}
      {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
      {isLink ? (
        <a
          href={value.startsWith('http://') || value.startsWith('https://') ? value : `${linkPrefix}${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:underline truncate min-w-0"
          onClick={e => e.stopPropagation()}
        >
          {value.includes("linkedin.com")
            ? "/" + value.replace(/^https?:\/\//, "").replace(/^(www\.)?linkedin\.com\/(in|company)\//, "").replace(/\/$/, "")
            : value.replace(/^https?:\/\//, "")}
        </a>
      ) : onDrillDown ? (
        <button
          className="text-accent hover:underline truncate text-left min-w-0"
          onClick={e => { e.stopPropagation(); onDrillDown(); }}
        >
          {value || ""}
        </button>
      ) : (
        <span className={cn("truncate min-w-0", !value && "text-muted-foreground/60")}>{value || placeholder || ""}</span>
      )}
      {canMutate && (
        <Pencil
          className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0"
          onClick={e => { e.stopPropagation(); setEditing(true); }}
        />
      )}
    </div>
  );
}
